import { useCallback, useEffect, useRef, useState } from 'react';
import { api, AdminRecording, AdminSettings, AdminStats, AdminUser } from './api';
import { UserView, VehicleView, RecordingView, SchemaBadge } from './AdminBrowse';
import { CalInstructionsModal } from './CalInstructionsModal';
import { SimulationPanel } from './SimulationPanel';

type View =
  | { kind: 'overview' }
  | { kind: 'user'; userId: number }
  | { kind: 'vehicle'; vehicleId: number; backUserId: number | null }
  | { kind: 'recording'; recordingId: number;
      backVehicleId: number | null; backUserId: number | null };

interface Props { onLogout: () => void; }

const AUTO_REFRESH_MS = 10_000;

function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(2)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
}
function formatTs(ms: number): string {
  return new Date(ms).toLocaleString();
}

export function AdminDashboard({ onLogout }: Props) {
  const [view, setView] = useState<View>({ kind: 'overview' });
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [recordings, setRecordings] = useState<AdminRecording[]>([]);
  const [settings, setSettings] = useState<AdminSettings | null>(null);
  const [maxDiskMb, setMaxDiskMb] = useState<number>(500);
  const [savingSettings, setSavingSettings] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [showCalInstructions, setShowCalInstructions] = useState(false);
  const [lastRefreshAt, setLastRefreshAt] = useState<number | null>(null);

  // Don't clobber the admin's in-progress edit of max_disk_mb on auto-refresh.
  // Sync the form input from the server only on the very first load.
  const firstLoadRef = useRef(true);

  const refresh = useCallback(async () => {
    try {
      const [s, u, r, st] = await Promise.all([
        api.get<AdminStats>('/api/admin/stats'),
        api.get<{ users: AdminUser[] }>('/api/admin/users'),
        api.get<{ recordings: AdminRecording[] }>('/api/admin/recordings'),
        api.get<AdminSettings>('/api/admin/settings'),
      ]);
      setStats(s);
      setUsers(u.users);
      setRecordings(r.recordings);
      setSettings(st);
      if (firstLoadRef.current) {
        setMaxDiskMb(st.max_disk_mb);
        firstLoadRef.current = false;
      }
      setLastRefreshAt(Date.now());
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(() => { refresh(); }, AUTO_REFRESH_MS);
    return () => clearInterval(id);
  }, [autoRefresh, refresh]);

  async function saveSettings() {
    setSavingSettings(true);
    setError(null);
    try {
      await api.put('/api/admin/settings', { max_disk_mb: maxDiskMb });
      await refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSavingSettings(false);
    }
  }

  async function logout() {
    try { await api.post('/api/logout'); } catch { /* ignore */ }
    onLogout();
  }

  return (
    <div className="app">
      <div className="header">
        <div className="brand">Engine Acoustics — Admin</div>
        <div className="row" style={{ alignItems: 'center' }}>
          <span className="muted" style={{ fontSize: '0.85rem' }}>
            {lastRefreshAt
              ? `updated ${new Date(lastRefreshAt).toLocaleTimeString()}`
              : 'loading…'}
          </span>
          <button
            className="secondary"
            onClick={() => setAutoRefresh((v) => !v)}
            title={autoRefresh
              ? `auto-refresh on (every ${AUTO_REFRESH_MS / 1000}s) — click to pause`
              : 'auto-refresh paused — click to resume'}
          >
            Auto-refresh: {autoRefresh ? `ON (${AUTO_REFRESH_MS / 1000}s)` : 'OFF'}
          </button>
          <button className="secondary" onClick={refresh}>Refresh now</button>
          <button
            className="secondary"
            onClick={() => setShowCalInstructions(true)}
            title="Calibration & usage procedures"
          >
            ? Cal instructions
          </button>
          <button className="secondary" onClick={logout}>Sign out</button>
        </div>
      </div>
      {showCalInstructions && (
        <CalInstructionsModal onClose={() => setShowCalInstructions(false)} />
      )}

      {error && <div className="panel error">{error}</div>}

      {view.kind === 'user' && (
        <UserView
          userId={view.userId}
          onBack={() => setView({ kind: 'overview' })}
          onOpenVehicle={(vehicleId) =>
            setView({ kind: 'vehicle', vehicleId, backUserId: view.userId })}
        />
      )}
      {view.kind === 'vehicle' && (
        <VehicleView
          vehicleId={view.vehicleId}
          onBack={() => view.backUserId != null
            ? setView({ kind: 'user', userId: view.backUserId })
            : setView({ kind: 'overview' })}
          onOpenRecording={(recordingId) =>
            setView({
              kind: 'recording', recordingId,
              backVehicleId: view.vehicleId, backUserId: view.backUserId,
            })}
        />
      )}
      {view.kind === 'recording' && (
        <RecordingView
          recordingId={view.recordingId}
          onBack={() => view.backVehicleId != null
            ? setView({
                kind: 'vehicle',
                vehicleId: view.backVehicleId,
                backUserId: view.backUserId,
              })
            : setView({ kind: 'overview' })}
        />
      )}

      {view.kind === 'overview' && stats && (
        <div className="panel">
          <div className="kpi">
            <div><div className="label">Users</div><div className="value">{stats.userCount}</div></div>
            <div><div className="label">Recordings</div><div className="value">{stats.recordingCount}</div></div>
            <div><div className="label">Disk used</div><div className="value">{formatBytes(stats.diskBytes)}</div></div>
            <div>
              <div className="label">Quota</div>
              <div className="value">{settings ? `${settings.max_disk_mb} MB` : '…'}</div>
            </div>
          </div>
        </div>
      )}

      {view.kind === 'overview' && (
        <SimulationPanel
          onOpenRecording={(recordingId) =>
            setView({
              kind: 'recording', recordingId,
              backVehicleId: null, backUserId: null,
            })}
        />
      )}

      {view.kind === 'overview' && (
        <>
          <div className="panel">
            <h3 style={{ marginTop: 0 }}>Settings</h3>
            <div className="row" style={{ alignItems: 'flex-end' }}>
              <div className="field" style={{ width: 200 }}>
                <label>Max disk (MB)</label>
                <input
                  type="number" min={10} max={1_000_000} step={10}
                  value={maxDiskMb}
                  onChange={(e) => setMaxDiskMb(Math.max(10, Number(e.target.value) || 10))}
                />
              </div>
              <div className="field">
                <button onClick={saveSettings} disabled={savingSettings}>
                  {savingSettings ? '…' : 'Save'}
                </button>
              </div>
            </div>
            <div className="muted">
              When total recordings exceed the limit, the oldest are deleted automatically.
            </div>
          </div>

          <div className="panel">
            <h3 style={{ marginTop: 0 }}>Users</h3>
            <table>
              <thead>
                <tr>
                  <th>ID</th><th>Username</th><th>Created</th>
                  <th>Recordings</th><th>Bytes used</th><th></th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id}>
                    <td>{u.id}</td>
                    <td>{u.username}</td>
                    <td className="muted">{formatTs(u.created_at)}</td>
                    <td>{u.recording_count}</td>
                    <td>{formatBytes(u.bytes_used)}</td>
                    <td>
                      <button
                        className="secondary"
                        onClick={() => setView({ kind: 'user', userId: u.id })}
                      >Browse →</button>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && <tr><td colSpan={6} className="muted">no users</td></tr>}
              </tbody>
            </table>
          </div>

          <div className="panel">
            <h3 style={{ marginTop: 0 }}>Recent recordings</h3>
            <table>
              <thead>
                <tr>
                  <th>ID</th><th>User</th><th>Created</th>
                  <th>Duration</th><th>Sample rate</th><th>Size</th>
                  <th>Status</th><th>Schema</th><th></th>
                </tr>
              </thead>
              <tbody>
                {recordings.map((r) => (
                  <tr key={r.id}>
                    <td>{r.id}</td>
                    <td>{r.username}</td>
                    <td className="muted">{formatTs(r.created_at)}</td>
                    <td>{r.duration_sec.toFixed(2)}s</td>
                    <td>{r.sample_rate} Hz</td>
                    <td>{formatBytes(r.size_bytes)}</td>
                    <td>{r.analysis_status}</td>
                    <td><SchemaBadge status={r.analysis_schema} /></td>
                    <td>
                      <button
                        className="secondary"
                        onClick={() => setView({
                          kind: 'recording', recordingId: r.id,
                          backVehicleId: null, backUserId: null,
                        })}
                      >Open →</button>
                    </td>
                  </tr>
                ))}
                {recordings.length === 0 && <tr><td colSpan={9} className="muted">no recordings</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
